import { useState, useEffect } from "react";
import { useQuery } from "@apollo/client";

import { CLIP_COMMENTS } from "MVVM/GraphQL/clipComments";
import { IClipCommentsData, IClipComment } from "./@types";

interface IClipCommentsViewModelProps {
  clipId: string;
}

const PAGE_SIZE = 25;

const ClipCommentsViewModel = ({ clipId }: IClipCommentsViewModelProps) => {
  const [allComments, setAllComments] = useState<IClipCommentsData | null>(
    null,
  );
  const [endCursor, setEndCursor] = useState<string | null>(null);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const {
    error,
    loading,
    fetchMore,
    data: queryData,
  } = useQuery(CLIP_COMMENTS, {
    variables: {
      id: clipId,
      first: PAGE_SIZE,
      after: null,
      last: null,
      before: null,
    },
    fetchPolicy: "cache-and-network",
  });

  useEffect(() => {
    if (queryData?.clip?.comments?.nodes) {
      const reversedNodes = [...queryData.clip.comments.nodes].reverse();

      const updatedComments = {
        ...queryData,
        clip: {
          ...queryData.clip,
          comments: {
            ...queryData.clip.comments,
            nodes: reversedNodes,
          },
        },
      };

      setAllComments(updatedComments);
      setEndCursor(queryData.clip.comments.pageInfo.endCursor);
      setHasNextPage(queryData.clip.comments.pageInfo.hasNextPage);
    }
  }, [queryData]);

  const loadMoreComments = async () => {
    if (!hasNextPage || isLoadingMore) {
      return;
    }
    setIsLoadingMore(true);

    try {
      const result = await fetchMore({
        variables: {
          id: clipId,
          first: PAGE_SIZE,
          after: endCursor,
        },
      });

      if (result.data?.clip?.comments) {
        const reversedNewComments = [
          ...result.data.clip.comments.nodes,
        ].reverse();
        const newPageInfo = result.data.clip.comments.pageInfo;

        if (reversedNewComments.length === 0) {
          setHasNextPage(false);
          return;
        }

        setAllComments((prev) => {
          if (!prev) return result.data;

          const updatedComments = {
            ...prev,
            clip: {
              ...prev.clip,
              comments: {
                ...prev.clip.comments,
                nodes: [...reversedNewComments, ...prev.clip.comments.nodes],
                pageInfo: newPageInfo,
              },
            },
          };

          setEndCursor(newPageInfo.endCursor);
          setHasNextPage(newPageInfo.hasNextPage);

          return updatedComments;
        });
      }
    } catch (error) {
      // Error loading more comments - could be handled with a toast notification
      // or other user feedback mechanism in the future
    } finally {
      setIsLoadingMore(false);
    }
  };

  return {
    data: allComments,
    error,
    loading,
    loadMoreComments,
    hasNextPage,
    isLoadingMore,
  };
};

export { ClipCommentsViewModel };
